import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, SkipForward, Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['all', 'mariage', 'festival', 'corporate', 'club', 'autre'];
const CATEGORY_LABELS = {
  all: 'Tous', mariage: 'Mariage', festival: 'Festival',
  corporate: 'Entreprise', club: 'Club', autre: 'Autre',
};

const resolveUrl = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

const MediaValidation = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('photo');
  const [category, setCategory] = useState('all');
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await adminApi.get('/api/admin/media');
      setItems(res.data);
    } catch (e) {
      toast({ title: 'Erreur de chargement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setIndex(0); }, [type, category]);

  const queue = useMemo(
    () => items.filter((i) => i.type === type && !i.reviewed && (category === 'all' || i.category === category)),
    [items, type, category]
  );

  const stats = useMemo(() => {
    const scope = items.filter((i) => i.type === type && (category === 'all' || i.category === category));
    return {
      total: scope.length,
      pending: scope.filter((i) => !i.reviewed).length,
      approved: scope.filter((i) => i.reviewed && i.published).length,
      rejected: scope.filter((i) => i.reviewed && !i.published).length,
    };
  }, [items, type, category]);

  const current = queue[Math.min(index, Math.max(queue.length - 1, 0))];

  const patch = async (id, data) => {
    setBusy(true);
    try {
      await adminApi.put(`/api/admin/media/${id}`, data);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...data } : i)));
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const approve = () => current && patch(current.id, { reviewed: true, published: true });
  const reject = () => current && patch(current.id, { reviewed: true, published: false });
  const skip = () => setIndex((i) => i + 1);

  const remove = async () => {
    if (!current || !window.confirm('Supprimer définitivement ce média ?')) return;
    setBusy(true);
    try {
      await adminApi.delete(`/api/admin/media/${current.id}`);
      setItems((prev) => prev.filter((i) => i.id !== current.id));
      toast({ title: 'Média supprimé' });
    } finally {
      setBusy(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Remettre tous les médias de cette sélection « à valider » ?')) return;
    const scope = items.filter((i) => i.type === type && (category === 'all' || i.category === category) && i.reviewed);
    setBusy(true);
    for (const item of scope) {
      // eslint-disable-next-line no-await-in-loop
      await adminApi.put(`/api/admin/media/${item.id}`, { reviewed: false });
    }
    setBusy(false);
    setIndex(0);
    load();
  };

  const preview = current
    ? current.type === 'video'
      ? current.thumbnail || (current.source === 'youtube' ? `https://img.youtube.com/vi/${current.url}/sddefault.jpg` : '')
      : resolveUrl(current.url)
    : '';

  return (
    <div data-testid="admin-media-validation">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Validation des médias</h1>
        <p className="text-gray-400 text-sm mt-1">
          Passez chaque photo et chaque vidéo une par une. Validez celles que vous gardez, refusez celles à masquer.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="validation-type-filters">
        {[{ k: 'photo', l: 'Photos' }, { k: 'video', l: 'Vidéos' }].map(({ k, l }) => (
          <button
            key={k}
            onClick={() => setType(k)}
            className={`px-5 py-2 rounded-full text-sm transition-colors ${type === k ? 'bg-orange-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
            data-testid={`validation-type-${k}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="validation-category-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${category === c ? 'bg-white text-black' : 'bg-transparent text-gray-500 border border-gray-700 hover:text-white'}`}
            data-testid={`validation-category-${c}`}
          >
            {CATEGORY_LABELS[c]}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6" data-testid="validation-stats">
        {[
          { label: 'Total', value: stats.total, color: 'text-white' },
          { label: 'À valider', value: stats.pending, color: 'text-orange-400' },
          { label: 'Validés', value: stats.approved, color: 'text-green-400' },
          { label: 'Refusés', value: stats.rejected, color: 'text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-gray-500 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-orange-500 animate-spin" /></div>
      ) : !current ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center" data-testid="validation-empty">
          <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-4" />
          <p className="text-white font-semibold">Tout est passé en revue dans cette sélection.</p>
          <p className="text-gray-500 text-sm mt-1">Changez de type ou de catégorie, ou recommencez la revue.</p>
          <Button onClick={resetAll} variant="outline" className="mt-5 border-gray-700 text-gray-300" data-testid="validation-reset">
            Recommencer la revue
          </Button>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden" data-testid="validation-card">
          <div className="bg-black flex items-center justify-center max-h-[55vh]">
            {current.type === 'video' && current.source !== 'youtube' ? (
              <video src={resolveUrl(current.url)} controls className="max-h-[55vh] w-full bg-black" data-testid="validation-video">
                <track kind="captions" />
              </video>
            ) : (
              <img src={preview} alt={current.title} className="max-h-[55vh] w-full object-contain" data-testid="validation-preview" />
            )}
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-purple-600/20 text-purple-300 border border-purple-500/30">
                {CATEGORY_LABELS[current.category] || current.category}
              </span>
              <span className="text-gray-500 text-xs">{queue.length} restant(s) à valider</span>
            </div>
            <p className="text-white font-semibold" data-testid="validation-title">{current.title || current.url}</p>
            <p className="text-gray-500 text-xs mt-1">{current.titleEn} · {current.titleIt} · {current.titleAr}</p>

            <div className="flex flex-wrap gap-3 mt-6">
              <Button onClick={approve} disabled={busy} className="bg-green-600 hover:bg-green-500" data-testid="validation-approve">
                <Check className="w-4 h-4 mr-2" /> Valider
              </Button>
              <Button onClick={reject} disabled={busy} className="bg-red-600 hover:bg-red-500" data-testid="validation-reject">
                <X className="w-4 h-4 mr-2" /> Refuser (masquer)
              </Button>
              <Button onClick={skip} disabled={busy} variant="outline" className="border-gray-700 text-gray-300" data-testid="validation-skip">
                <SkipForward className="w-4 h-4 mr-2" /> Passer
              </Button>
              <Button onClick={remove} disabled={busy} variant="outline" className="border-red-900 text-red-400 hover:bg-red-950" data-testid="validation-delete">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaValidation;
